'use client';

/**
 * API namespace `payouts` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L6071-L6172).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const payouts = {
  /**
   * POST /api/seller/payout/request — Request a seller payout
   * (withdraws from `wallet.sellerBalance`, fee='0' ALWAYS per INV-MKT-08).
   *
   * The seller-specific withdrawal flow. Uses `clientRequestId` (UUID
   * v4) for idempotency (Bug #14 fix pattern — same as the general
   * payout flow). The seller must have `kycLevel >= 1` (SPEC-NEGOCIO
   * §4.5) + sufficient `wallet.sellerBalance`.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` + KYC gate +
   * `requireRecent2FA` (5min 2FA — INV-SEC-06 §9.6 financial-grade op).
   *
   * Spec ref: SPEC-NEGOCIO §10.4, §4.3 INV-MKT-08; ADR-061.
   */
  async request(
    data: SellerPayoutRequestBody,
    signal?: AbortSignal,
  ): Promise<{
    payoutId: string;
    status: 'awaiting_review' | 'pending' | 'processing' | 'completed' | 'failed' | 'expired';
    amount: string;
    fee: string;        // ALWAYS '0' for sellers (INV-MKT-08)
    netAmount: string;
    network: string;
    walletAddress: string;
    createdAt: string;
  }> {
    return apiFetch('/seller/payout/request', {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * GET /api/seller/payout/balance — Get the seller's marketplace-sales
   * balance + lifetime tally.
   *
   * Returns `SellerPayoutBalanceResponse` — `sellerBalance` (current
   * retirable from marketplace sales) + `totalEarnedFromSales` (lifetime
   * tally). Distinct from `wallet.totalBalance` (which mixes matrix +
   * bonus earnings + deposits + gift cards).
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-NEGOCIO §10.4; ADR-061.
   */
  async getBalance(signal?: AbortSignal): Promise<SellerPayoutBalanceResponse> {
    return apiFetch<SellerPayoutBalanceResponse>('/seller/payout/balance', { signal });
  },
  /**
   * GET /api/seller/payout/history — Get the seller's recent payout
   * history (seller-specific withdrawals from `wallet.sellerBalance`).
   *
   * Query: `?limit=10` (max 100). Returns the same `PayoutDto` shape as
   * `api.getPayoutHistory` — the seller payouts are stored in the SAME
   * `payouts` collection with `fee='0'`, distinguished by the
   * `refType: 'seller_payout'` audit log entry.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-NEGOCIO §10.4; ADR-061.
   */
  async getHistory(
    params?: { limit?: number },
    signal?: AbortSignal,
  ): Promise<{ items: PayoutDto[]; total: number }> {
    const qs = params?.limit ? `?limit=${encodeURIComponent(params.limit)}` : '';
    return apiFetch(`/seller/payout/history${qs}`, { signal });
  },
};
